/**
 * Bağlantının QR kodu.
 *
 * Neden var: uygulayıcı ödev atadı, çocuk tableti getirdi. Jetonu
 * elle yazdırmak ya da bağlantıyı tek tek açtırmak gerçekte
 * işlemiyor — jeton uzun, çocuk yazamaz, uygulayıcı her seferinde
 * tableti alıp adres çubuğuna yazar. Kodu panelde gösterip tabletin
 * kamerasıyla okutmak bu adımı tamamen kaldırıyor.
 *
 * KOD TARAYICIDA ÜRETİLİYOR. Bir QR servisine (api.qrserver, Google
 * Charts vb.) adres göndermek, ödev jetonunu üçüncü bir tarafa
 * vermek demekti; o jeton çocuğun ölçüm/egzersiz oturumunu açıyor.
 * Sunucudan resim istemek de jetonu bir istek yoluna daha yazardı.
 * Burada jeton zaten panelin elinde ve hiçbir yere gitmiyor.
 *
 * Jetonun tek kullanımlık ve kısa ömürlü olması DEĞİŞMİYOR: QR
 * yalnızca aynı bağlantıyı okutmanın bir yolu, yeni bir kapı değil.
 */

import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'

export function Kare({ adres, boyut = 180 }: { adres: string; boyut?: number }) {
  const tuval = useRef<HTMLCanvasElement>(null)
  const [hata, setHata] = useState(false)

  useEffect(() => {
    const t = tuval.current
    if (!t) return
    setHata(false)
    /**
     * Hata düzeltme seviyesi M: tabletin kamerası panelin ekranına
     * belirli bir açıyla bakıyor ve ekran parlaması oluyor. En düşük
     * seviye (L) bu koşulda okumuyor; en yüksek (H) kareyi
     * gereksiz yoğunlaştırıp küçük ekranda okunmaz yapıyor.
     */
    QRCode.toCanvas(t, adres, {
      width: boyut,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#161b22ff', light: '#ffffffff' },
    }).catch(() => setHata(true))
  }, [adres, boyut])

  if (hata) {
    return (
      <p className="alt" style={{ margin: 0 }}>
        QR kodu üretilemedi; bağlantıyı kopyalayıp kullanabilirsiniz.
      </p>
    )
  }

  return (
    <canvas
      ref={tuval}
      width={boyut}
      height={boyut}
      style={{
        width: boyut,
        height: boyut,
        /** Beyaz zemin şart: koyu temada kod okunmaz hale geliyor */
        background: '#fff',
        border: '1px solid var(--line)',
        borderRadius: 8,
        padding: 6,
      }}
      aria-label="Bağlantının QR kodu"
    />
  )
}

/**
 * Tam ekran QR.
 *
 * Tableti uygulayıcının masasına yaklaştırmak her zaman kolay
 * değil; kodu büyütmek odanın öbür ucundan okutmayı mümkün kılıyor.
 * Ekranı tamamen kaplıyor, çünkü etrafındaki panel içeriği hem
 * dikkat dağıtıyor hem de çocuk tableti tutarken dosya bilgisi
 * ekranda açık kalıyor.
 */
export function KareBuyuk({ adres, onKapat }: { adres: string; onKapat: () => void }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onKapat()
    }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onKapat])

  return (
    <div
      onClick={onKapat}
      style={{
        position: 'fixed',
        inset: 0,
        background: '#fff',
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        cursor: 'pointer',
      }}
    >
      <Kare adres={adres} boyut={Math.min(520, Math.round(window.innerHeight * 0.6))} />
      <p style={{ color: '#161b22', margin: 0, fontSize: 15 }}>
        Tabletin kamerasını buraya tutun.
      </p>
      <p style={{ color: '#5b6676', margin: 0, fontSize: 13 }}>
        Kapatmak için ekrana dokunun ya da Esc'e basın.
      </p>
    </div>
  )
}
